import {addVectors, midPoint, scaleVector, subtractVectors, Vector} from "./vector"
import {baseTriangle, baseTriangleHeight} from "./sierpinski"
import {Rectangle, rectangleTriangleIntersect, splitTriangle, Triangle} from "./geometry"

/**
 * Type describing the current centre and zoom level, i.e. what part of the Sierpinski triangle pattern is visible
 * in the SVG.
 */
export type ViewBoxState = {
    /**
     * The centre point of the current view rectangle in coordinate system defining the base triangle.
     */
    centre: Vector

    /**
     * Size of the SVG viewport relative to the base triangle size.
     */
    scale: number
}

const baseWidth = baseTriangle.right[0] - baseTriangle.left[0]
const baseHeight = baseTriangle.left[1] - baseTriangle.top[1]

// Initially, fit the whole triangle into the SVG.
export const initialViewBoxState: ViewBoxState = {
    centre: midPoint(baseTriangle.top, midPoint(baseTriangle.left, baseTriangle.right)),
    scale: 1
}


// Converts a vector in browser pixel units to a vector in viewport coordinates
const pixelsToViewport = (svgSize: Vector, scale: number, vector: Vector): Vector => {
    // SVG is set to force uniform scaling, so we need to work out which dimension to base our scale on
    const useWidth = svgSize[1] / svgSize[0] > baseHeight / baseWidth
    const scaleFactor =  scale * (useWidth ? baseWidth / svgSize[0] : baseHeight / svgSize[1])
    return scaleVector(scaleFactor, vector)
}

const {rightChild, leftChild, topChild} = splitTriangle(baseTriangle)

const baseChildren = [
    {child: 'top', triangle: topChild},
    {child: 'left', triangle: leftChild},
    {child: 'right', triangle: rightChild}
]

const leftChildChildren = splitTriangle(leftChild)
const rightChildChildren = splitTriangle(rightChild)
const topChildChildren = splitTriangle(topChild)

// Triangle formed by joining the midpoints of the base triangle's children's inner edges
const centreTriangle: Triangle = {
    right: [0.125, 0.75 * baseTriangleHeight],
    left: [-0.125, 0.75 * baseTriangleHeight],
    top: [0, 0.5 * baseTriangleHeight]
}

// Scale up the ViewBoxState by one fractal level and offset origin
const scaleUpViewBox = ({centre, scale}: ViewBoxState, offset: Vector) => ({
    scale: scale * 2,
    centre: scaleVector(2, subtractVectors(centre, offset))
})

/*
 * This function takes advantage of the Sierpinski triangle's self-similarity to enable infinite zoom. If only one child
 * of a triangle is visible, the viewport is scaled up by one fractal level to reveal more detail. If two children are
 * intersected, their child triangles and the centre triangle are tested for visibility. If suitable, the viewport is
 * then offset and scaled around the vertex shared by the intersected children.
 */
const maximiseDetail = (svgSize: Vector, state: ViewBoxState): ViewBoxState => {
    const scaledSvg = pixelsToViewport(svgSize, state.scale, svgSize)

    // Viewport rectangle in SVG coordinates
    const viewportRect: Rectangle = {
        topLeft: subtractVectors(state.centre, scaleVector(0.5, scaledSvg)),
        size: scaledSvg
    }

    const intersectedChildren = baseChildren
        .filter(({triangle}) => rectangleTriangleIntersect(viewportRect, triangle))
        .map(({child}) => child)

    if (intersectedChildren.length === 1) {
        const intersectedChild = intersectedChildren[0]

        // If only one child triangle is intersected, it is safe to zoom in on that triangle
        if (intersectedChild === 'top') {
            return maximiseDetail(svgSize, scaleUpViewBox(state, [0, 0]))
        } else if (intersectedChild === 'left') {
            return maximiseDetail(svgSize, scaleUpViewBox(state, [-0.25, 0.5 * baseTriangleHeight]))
        } else {
            return maximiseDetail(svgSize, scaleUpViewBox(state, [0.25, 0.5 * baseTriangleHeight]))
        }
    } else if (intersectedChildren.length === 2) {
        const intersectsCentre = rectangleTriangleIntersect(viewportRect, centreTriangle)

        /*
         * If two child triangles are intersected, it is safe to zoom about their shared vertex if the viewport only
         * intersects the children of those child triangles that meet at that vertex and does not intersect the
         * invisible triangle at the centre.
         */
        if (intersectsCentre) {
            return state
        } else {
            if (intersectedChildren.indexOf('left') === -1) {
                const topCorrect = rectangleTriangleIntersect(viewportRect, topChildChildren.rightChild)
                    && !rectangleTriangleIntersect(viewportRect, topChildChildren.leftChild)
                    && !rectangleTriangleIntersect(viewportRect, topChildChildren.topChild)

                const rightCorrect = rectangleTriangleIntersect(viewportRect, rightChildChildren.topChild)
                    && !rectangleTriangleIntersect(viewportRect, rightChildChildren.leftChild)
                    && !rectangleTriangleIntersect(viewportRect, rightChildChildren.rightChild)

                return topCorrect && rightCorrect
                    ? maximiseDetail(svgSize, scaleUpViewBox(state, [0.125, 0.25 * baseTriangleHeight]))
                    : state
            } else if (intersectedChildren.indexOf('right') === -1) {
                const topCorrect = rectangleTriangleIntersect(viewportRect, topChildChildren.leftChild)
                    && !rectangleTriangleIntersect(viewportRect, topChildChildren.rightChild)
                    && !rectangleTriangleIntersect(viewportRect, topChildChildren.topChild)

                const leftCorrect = rectangleTriangleIntersect(viewportRect, leftChildChildren.topChild)
                    && !rectangleTriangleIntersect(viewportRect, leftChildChildren.rightChild)
                    && !rectangleTriangleIntersect(viewportRect, leftChildChildren.leftChild)

                return topCorrect && leftCorrect
                    ? maximiseDetail(svgSize, scaleUpViewBox(state, [-0.125, 0.25 * baseTriangleHeight]))
                    : state
            } else {
                const rightCorrect = rectangleTriangleIntersect(viewportRect, rightChildChildren.leftChild)
                    && !rectangleTriangleIntersect(viewportRect, rightChildChildren.rightChild)
                    && !rectangleTriangleIntersect(viewportRect, rightChildChildren.topChild)

                const leftCorrect = rectangleTriangleIntersect(viewportRect, leftChildChildren.rightChild)
                    && !rectangleTriangleIntersect(viewportRect, leftChildChildren.topChild)
                    && !rectangleTriangleIntersect(viewportRect, leftChildChildren.leftChild)

                return leftCorrect && rightCorrect
                    ? maximiseDetail(svgSize, scaleUpViewBox(state, [0, 0.5 * baseTriangleHeight]))
                    : state
            }
        }
    } else {
        return state
    }
}

/**
 * Convert a ViewBoxState to a string suitable for use as the SVG viewBox attribute.
 */
export const viewBoxStateToAttr = (svgSize: Vector, inputState: ViewBoxState): string => {
    const {centre, scale} = maximiseDetail(svgSize, inputState)
    const width = baseWidth * scale
    const height = baseHeight * scale

    return [
        centre[0] - 0.5 * width,
        centre[1] - 0.5 * height,
        width,
        height
    ].join(' ')
}

/**
 * A curried function computing the next ViewBoxState when the SVG is dragged.
 * @param {Vector} svgSize Current SVG dimensions in pixels
 * @param {Vector} movement Mouse movement vector in pixels
 * @returns {({centre, scale}: ViewBoxState) => ViewBoxState} Function from old state to new state
 */
export const pan = (svgSize: Vector, movement: Vector) => ({centre, scale}: ViewBoxState): ViewBoxState => {
    const viewportMovement = pixelsToViewport(svgSize, scale, movement)

    return {
        scale,
        centre: subtractVectors(centre, viewportMovement)
    }
}

const zoomSpeed: number = 1000

/**
 * A curried function computing the next ViewBoxState when the SVG is zoomed.
 * @param svgSize Current SVG dimensions in pixels
 * @param location Location of mouse event in pixel coordinates
 * @param {number} deltaY Mouse wheel movement
 * @returns {({scale, centre}: ViewBoxState) => ViewBoxState} Function from old state to new state
 */
export const zoom = (svgSize: Vector, location: Vector, deltaY: number) => ({scale, centre}: ViewBoxState): ViewBoxState => {
    // Vector from centre of SVG to cursor location in pixels
    const centreOffsetPixels = subtractVectors(location, scaleVector(0.5, svgSize))

    // Cursor location in viewport coordinates
    const cursorPosition = addVectors(centre, pixelsToViewport(svgSize, scale, centreOffsetPixels))

    const newScale = scale * Math.exp(deltaY / zoomSpeed)

    // New centre preserves pixel coordinates of cursor location, centring zoom on the cursor
    const newCentre = subtractVectors(cursorPosition, pixelsToViewport(svgSize, newScale, centreOffsetPixels))

    return {centre: newCentre, scale: newScale}
}



// WEBPACK FOOTER //
// ./src/view-box.ts